import React from 'react';
import { CalendarDays, CheckCircle2, FileText, MessageSquare, PenSquare, Trash2, User, UserRound, ClipboardCheck, Activity } from 'lucide-react';
import { PulsePanel, PulseEmpty } from './PulsePanel';
import { PulseRing } from './PulseRing';
import { fmtTime, usePaged, PageDots } from './pulseUtils';
import { useAutoScroll } from './useAutoScroll';

const RING_COLORS = ['#922A8C', '#19B5C9', '#2F9BEA', '#F5B400', '#EF3A5D'];
const KIND = {
  case_note: { icon: FileText, color: '#F08A24', bg: '#fff1e4' }, document: { icon: FileText, color: '#1E88E5', bg: '#e8f2fd' },
  task: { icon: CheckCircle2, color: '#F5B400', bg: '#fff7dc' }, appointment: { icon: CalendarDays, color: '#EF3A5D', bg: '#ffe9ed' },
  participant: { icon: UserRound, color: '#22B04B', bg: '#e6f7ea' }, caseload_review: { icon: ClipboardCheck, color: '#922A8C', bg: '#f6e9f5' },
  communication: { icon: MessageSquare, color: '#19B5C9', bg: '#e3f7fa' }, other: { icon: Activity, color: '#6b6270', bg: '#f1eff2' },
};
const iconFor = (s) => (s.kind === 'case_note' && /deleted/i.test(s.activity) ? { ...KIND.case_note, icon: Trash2, color: '#EF3A5D', bg: '#ffe9ed' }
  : s.kind === 'case_note' && /edited/i.test(s.activity) ? { ...KIND.case_note, icon: PenSquare, color: '#922A8C', bg: '#f6e9f5' } : KIND[s.kind] || KIND.other);

const StaffRings = ({ people, rotateMs }) => {
  const { slice, page, pages } = usePaged(people, 5, rotateMs);
  return (
    <div className="px-6 pt-3" data-testid="pulse-staff-rings">
      <div className="text-[25px] font-bold">Staff Activity Today</div>
      <div className="text-[16px] text-[#5c5462]">% of all staff actions recorded today</div>
      <div key={page} className="grid grid-cols-5 gap-2 mt-3 pulse-fade-in">
        {slice.map((p, i) => (
          <div key={p.name} className="flex flex-col items-center text-center" data-testid="pulse-staff-ring-item">
            <PulseRing percent={p.percent} size={84} stroke={8} color={RING_COLORS[(page * 5 + i) % 5]} track="#eeeaf0"><span className="text-[19px]">{p.percent}%</span></PulseRing>
            <span className="mt-1.5 w-9 h-9 rounded-full text-white text-[14px] font-bold flex items-center justify-center border-2 border-white" style={{ background: RING_COLORS[(page * 5 + i) % 5] }}>{p.initials}</span>
            <span className="text-[16px] font-semibold leading-tight mt-1 line-clamp-2">{p.name}</span>
          </div>
        ))}
      </div>
      <PageDots page={page} pages={pages} testid="pulse-staff-ring-pages" />
    </div>
  );
};

const StreamRow = ({ s }) => {
  const k = iconFor(s); const Icon = k.icon;
  return (
    <div className="grid grid-cols-[42px_160px_minmax(0,1fr)_88px] items-center border-b border-[#f3eff4] py-[6px] text-[16px] leading-tight min-h-[52px] shrink-0 pulse-row-in" data-testid="pulse-activity-row">
      <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: k.bg }}><Icon size={19} color={k.color} /></span>
      <span className="line-clamp-2 pr-2 font-medium">{s.user_name}</span>
      <span className="line-clamp-2 pr-2">{s.activity}</span>
      <span className="text-right tabular-nums">{fmtTime(s.timestamp)}</span>
    </div>
  );
};

const Stream = ({ stream }) => {
  const scrollRef = useAutoScroll();
  return (
    <div className="px-6 pt-3 flex-1 min-h-0 flex flex-col" data-testid="pulse-activity-stream">
      <div className="flex items-center"><div className="text-[25px] font-bold">Recent Activity Stream</div><div className="flex-1" />
        <span className="border border-[#d9cfdd] rounded-lg px-3 py-0.5 text-[16px]">All Users</span></div>
      <div className="grid grid-cols-[42px_160px_minmax(0,1fr)_88px] text-[16px] font-bold text-[#3a3340] border-b border-[#ece6ee] py-1.5 mt-1 pr-2"><span /><span>User</span><span>Activity</span><span className="text-right">Time</span></div>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto pulse-scroll pr-2 flex flex-col" data-testid="pulse-activity-scroll">
        {stream.length === 0 ? <PulseEmpty text="No staff activity recorded yet today" testid="pulse-activity-empty" /> : stream.slice().reverse().map((s, i) => <StreamRow key={s.id || `${s.timestamp}-${i}`} s={s} />)}
      </div>
    </div>
  );
};

export const PulseUserActivity = ({ data, rotateSeconds }) => (
  <PulsePanel icon={User} title="User Activity" pill="Today" testid="pulse-user-activity" className="h-full">
    <StaffRings people={data.people} rotateMs={(rotateSeconds || 8) * 1000} />
    <Stream stream={data.stream} />
  </PulsePanel>
);
